/**
 * components/FieldList.tsx - Field List Component
 * 
 * CHANGE HISTORY:
 * - 251103_1044 [pending] danielbeer: Initial FieldList component
 * 
 * Displays a list of source or target fields with mapping status.
 */

import React from 'react';
import type { Field, Mapping } from '../types';

export interface FieldListProps {
  fields: Field[];
  type: 'source' | 'target';
  mappings: Mapping[];
  selectedMappings: string[];
  onFieldClick: (field: Field) => void;
  onMappingClick: (mapping: Mapping) => void;
  getMappedStatus: (fieldId: string) => boolean;
  getMappingsForField: (fieldId: string) => Mapping[];
  showConfidence?: boolean;
  theme?: 'light' | 'dark';
}

export const FieldList: React.FC<FieldListProps> = ({
  fields,
  type,
  mappings,
  selectedMappings,
  onFieldClick,
  onMappingClick,
  getMappedStatus,
  getMappingsForField,
  showConfidence = true,
  theme = 'light'
}) => {
  return (
    <div className={`field-list field-list--${type} field-list--${theme}`}>
      {fields.map(field => {
        const isMapped = getMappedStatus(field.id);
        const fieldMappings = getMappingsForField(field.id);
        
        return (
          <div
            key={field.id}
            className={`field-item ${isMapped ? 'field-item--mapped' : ''}`}
            onClick={() => onFieldClick(field)}
            data-field-id={field.id}
          >
            <div className="field-item__header">
              <span className="field-item__icon">
                {isMapped ? '✓' : '○'}
              </span>
              <span className="field-item__name">
                {field.name}
                {field.required && <span className="field-item__required">*</span>}
              </span>
              <span className="field-item__type">{field.type}</span>
            </div>
            
            {field.description && (
              <div className="field-item__description">
                {field.description}
              </div>
            )}
            
            {field.sample && (
              <div className="field-item__sample">
                Sample: <code>{JSON.stringify(field.sample)}</code>
              </div>
            )}
            
            {fieldMappings.length > 0 && showConfidence && (
              <div className="field-item__mappings">
                {fieldMappings.map(mapping => (
                  <div
                    key={mapping.id}
                    className={`field-item__mapping ${
                      selectedMappings.includes(mapping.id) ? 'field-item__mapping--selected' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMappingClick(mapping);
                    }}
                  >
                    {mapping.confidence && (
                      <span className="field-item__confidence">
                        {mapping.confidence}%
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
